import { useEffect } from 'react'

import { skipToken } from '@reduxjs/toolkit/query/react'

import { selectUserId } from '~/auth/slices/auth.slice'
import { useGetCurrentGradeQuery, useGetCurriculumDataQuery } from '~/curriculum/apis/curriculum.api'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { setCurriculumSubjectContentActionCreator } from '~/curriculum/slice/curriculum.slice'
import { curriculumSubjectContentDefault } from '~/curriculum/constants/curriculum.const'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { getCurriculumRelevantCountryCodeAndGradeId } from '~/shared/sanity/utils/curriculum.utils'

import useGetGradeOverviewPageRoute from './useGetGradeOverviewPageRoute'
import useGetGradePageRoute from './useGetGradePageRoute'

export default function useGetCurriculumRedirectionUrl({
  specificGrade,
  specificCountryCode,
  specificSubjectOrder,
  specificSubTopic,
  specificSubTopicOrder,
}: {
  specificGrade?: string | number | undefined
  specificCountryCode?: string
  specificSubjectOrder?: number | string | undefined
  specificSubTopic?: string | undefined
  specificSubTopicOrder?: number | string | undefined
} = {}): string {
  const userId = useAppSelector(selectUserId)

  const usersCountryCode = useGetUserChosenCurriculumCountryCode()
  const countryCode = specificCountryCode ?? usersCountryCode
  const dispatch = useAppDispatch()
  const selectGradeUrl = useGetGradePageRoute()

  const { data: gradeData } = useGetCurrentGradeQuery(!userId || specificGrade ? skipToken : undefined)
  const grade = specificGrade ?? gradeData?.grade

  const { data } = useGetCurriculumDataQuery(
    {
      countryCode: getCurriculumRelevantCountryCodeAndGradeId(specificCountryCode ?? usersCountryCode)
        .curriculumCountryCode,
      grade: grade as number,
    },
    { skip: !userId || !countryCode || !grade },
  )

  const subjectOrder = specificSubjectOrder ?? data?.subjectOrder

  const curriculumGradeOverviewUrl = useGetGradeOverviewPageRoute({ grade: grade ?? 0 })

  const baseSubjectUrl = subjectOrder ? `${curriculumGradeOverviewUrl}/${subjectOrder}` : ''

  useEffect(() => {
    if (baseSubjectUrl) {
      dispatch(
        setCurriculumSubjectContentActionCreator({ ...curriculumSubjectContentDefault, subjectUrl: baseSubjectUrl }),
      )
    }
  }, [dispatch, baseSubjectUrl])

  if (!grade) return selectGradeUrl

  if (!subjectOrder) return curriculumGradeOverviewUrl

  const goesToSubTopic = Boolean(specificSubTopic && specificSubTopicOrder)

  return goesToSubTopic ? `${baseSubjectUrl}/${specificSubTopicOrder}` : baseSubjectUrl
}
